import React from 'react';
import { X, User, LogOut, FileText, Clock, CheckCircle } from 'lucide-react';
import { Card, Badge } from '../design-system';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LanguageSwitcher } from '../LanguageSwitcher';

export const RightDrawer = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <aside
        className={`
          fixed lg:sticky top-0 right-0 z-50
          h-screen w-80 bg-gray-50 border-l border-gray-200
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{t('common.appName')}</h2>
            <button
              onClick={onClose}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
            {/* User Profile Section */}
            <Card padding="default">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-primary-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
              </div>
              
              <div className="pt-2 border-t border-gray-200">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  {t('auth.logout')}
                </button>
              </div>
            </Card>

            {/* Language Switcher Section */}
            <Card padding="default">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('language.selectLanguage')}</h3>
              <div className="flex justify-center">
                <LanguageSwitcher />
              </div>
            </Card>

            {/* Quick Stats */}
            <Card padding="sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('rightDrawer.quickStats')}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">{t('rightDrawer.totalPlans')}</span>
                  </div>
                  <Badge variant="primary">-</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-gray-600">{t('rightDrawer.inProgress')}</span>
                  </div>
                  <Badge variant="warning">-</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-gray-600">{t('rightDrawer.completed')}</span>
                  </div>
                  <Badge variant="success">-</Badge>
                </div>
              </div>
            </Card>

            {/* Help Section */}
            <Card padding="sm" className="bg-primary-50 border-primary-100">
              <h3 className="text-sm font-semibold text-primary-900 mb-2">{t('header.needHelp')}</h3>
              <p className="text-xs text-primary-700">
                {t('header.checkDocumentation')}
              </p>
            </Card>
          </div>
        </div>
      </aside>
    </>
  );
};
